<script setup lang="ts">
import { computed } from 'vue'

import type { InsightHistoryFilters } from '../../../composables/useInsightHistory'
import type { InsightResult } from '../../../models/insight.types'
import type { WorkbenchAsyncState } from '../../../models/workbench.types'
import type {
  InsightHistoryGroup,
  InsightHistoryGroupBy,
} from '../../../service/insightPresentation'
import { outcomePresentation, severityPresentation } from '../../../service/insightPresentation'
import InsightElementList from '../../display/InsightElementList.vue'

import { useI18n } from '@/shared/i18n'
import { useFormat } from '@/shared/lib/format'
import { Alert } from '@/shared/ui/alert'
import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { RichTextViewer } from '@/shared/ui/editor'
import { SegmentedControl } from '@/shared/ui/segmented-control'
import { Select, type SelectOption } from '@/shared/ui/select'
import { Skeleton } from '@/shared/ui/skeleton'

const props = defineProps<{
  groups: readonly InsightHistoryGroup[]
  groupBy: InsightHistoryGroupBy
  selected: InsightResult | null
  filters: InsightHistoryFilters
  state: WorkbenchAsyncState
  detailState: WorkbenchAsyncState
  detailTab: string
}>()
const emit = defineEmits<{
  'update:filters': [value: InsightHistoryFilters]
  'update:groupBy': [value: InsightHistoryGroupBy]
  'update:detailTab': [value: string]
  apply: []
  clear: []
  retry: []
  select: [result: InsightResult]
}>()

const { t } = useI18n()
const format = useFormat()
const options = (keys: string[], prefix: string): SelectOption<string>[] => [
  { value: '', label: t('insights.sysadmin.filters.all') },
  ...keys.map((value) => ({ value, label: t(`${prefix}.${value}`) })),
]
const outcomeOptions: SelectOption<string>[] = [
  { value: '', label: t('insights.sysadmin.filters.all') },
  ...['revenue_gain', 'roas_uplift', 'cost_savings', 'lost_revenue'].map((value) => ({
    value,
    label: t(outcomePresentation(value).labelKey),
  })),
]
const severityOptions: SelectOption<string>[] = [
  { value: '', label: t('insights.sysadmin.filters.all') },
  ...['needs_attention', 'opportunity', 'informational', 'actual', 'other'].map((value) => ({
    value,
    label: t(severityPresentation(value).labelKey),
  })),
]
const platformOptions = options(['all', 'google', 'meta', 'tiktok'], 'insights.sysadmin.platform')
const statusOptions = options(
  ['pending', 'seen', 'clicked', 'dismissed', 'applied'],
  'insights.sysadmin.resultStatus',
)
const tabOptions = computed<SelectOption<string>[]>(() =>
  (props.selected?.tabs ?? []).map((tab) => ({ value: tab.key, label: tab.label || tab.key })),
)
const selectedOutcome = computed(() => outcomePresentation(props.selected?.outcome_category))
const selectedSeverity = computed(() => severityPresentation(props.selected?.severity))

function field<K extends keyof InsightHistoryFilters>(key: K) {
  return computed({
    get: () => props.filters[key],
    set: (value: InsightHistoryFilters[K]) =>
      emit('update:filters', { ...props.filters, [key]: value }),
  })
}
const outcome = field('outcome')
const severity = field('severity')
const platform = field('platform')
const status = field('status')
const detailTabModel = computed({
  get: () => props.detailTab,
  set: (value: string | null) => emit('update:detailTab', value ?? ''),
})
const groupByModel = computed({
  get: () => props.groupBy,
  set: (value: string | number | boolean | null) => {
    if (value === 'outcome' || value === 'severity') emit('update:groupBy', value)
  },
})
const groupByOptions: SelectOption<InsightHistoryGroupBy>[] = [
  { value: 'outcome', label: t('insights.sysadmin.history.groupOutcome') },
  { value: 'severity', label: t('insights.sysadmin.history.groupSeverity') },
]

function title(result: InsightResult): string {
  return result.title || result.label || result.config_enum || t('insights.detail.untitled')
}

function formatImpact(result: InsightResult): string {
  const value = Number(result.impact_score)
  if (!Number.isFinite(value)) return t('insights.sysadmin.preview.notAvailable')
  if (result.impact_format === 'currency') return format.formatCurrency(value, { maxFrac: 2 })
  if (result.impact_format === 'percentage') {
    return format.formatPercent(value, { scale: 'percent', maxFrac: 2 })
  }
  return format.formatNumber(value, { maxFrac: 2, heavyDefault: true })
}
</script>

<template>
  <section class="history-panel grid gap-md min-w-0" data-testid="insight-history-panel">
    <SegmentedControl
      v-model="groupByModel"
      :label="t('insights.sysadmin.history.groupBy')"
      :options="groupByOptions"
      size="sm"
    />
    <form
      class="history-panel__filters grid grid-cols-[repeat(4,_minmax(8rem,_1fr))_auto] items-end gap-sm max-lg:grid-cols-[minmax(0,_1fr)]"
      @submit.prevent="emit('apply')"
    >
      <Select
        v-model="outcome"
        :label="t('insights.sysadmin.filters.outcome')"
        :options="outcomeOptions"
      />
      <Select
        v-model="severity"
        :label="t('insights.sysadmin.filters.severity')"
        :options="severityOptions"
      />
      <Select
        v-model="platform"
        :label="t('insights.sysadmin.filters.platform')"
        :options="platformOptions"
      />
      <Select
        v-model="status"
        :label="t('insights.sysadmin.filters.status')"
        :options="statusOptions"
      />
      <div class="history-panel__filter-actions flex gap-xs">
        <Button type="submit" size="sm">{{ t('insights.sysadmin.filters.apply') }}</Button>
        <Button type="button" size="sm" variant="ghost" @click="emit('clear')">
          {{ t('insights.sysadmin.filters.clear') }}
        </Button>
      </div>
    </form>

    <div
      v-if="state === 'loading'"
      class="history-panel__loading grid gap-md min-w-0"
      aria-busy="true"
    >
      <Skeleton v-for="index in 4" :key="index" height="4rem" />
    </div>
    <Alert v-else-if="state === 'error'" tone="danger">
      {{ t('insights.sysadmin.history.error') }}
      <Button size="sm" variant="ghost" @click="emit('retry')">{{
        t('insights.error.retry')
      }}</Button>
    </Alert>
    <p v-else-if="state === 'empty'" class="history-panel__empty p-xl text-muted text-center">
      {{ t('insights.sysadmin.history.empty') }}
    </p>
    <div
      v-else
      class="history-panel__content grid grid-cols-[minmax(14rem,_0.7fr)_minmax(0,_1.6fr)] gap-md max-lg:grid-cols-[minmax(0,_1fr)]"
    >
      <nav
        class="history-panel__groups grid gap-md min-w-0 content-start max-h-192 overflow-auto"
        :aria-label="t('insights.sysadmin.history.groups')"
      >
        <section v-for="group in groups" :key="group.key">
          <h3>{{ t(group.labelKey) }}</h3>
          <div v-for="bucket in group.buckets" :key="bucket.key">
            <h4>
              <Badge :tone="bucket.tone" size="sm">{{ t(bucket.labelKey) }}</Badge>
            </h4>
            <button
              v-for="result in bucket.items"
              :key="result.id"
              type="button"
              class="history-panel__result flex w-full justify-between gap-sm p-sm border border-border rounded-sm bg-surface text-fg text-start"
              :class="{ 'is-selected': result.id === selected?.id }"
              @click="emit('select', result)"
            >
              <span>{{ title(result) }}</span>
              <strong>{{ formatImpact(result) }}</strong>
            </button>
          </div>
        </section>
      </nav>

      <article
        class="history-panel__detail grid gap-md min-w-0 content-start p-md border border-border rounded-md"
      >
        <p v-if="!selected">{{ t('insights.sysadmin.history.select') }}</p>
        <template v-else>
          <header>
            <div>
              <h3>{{ title(selected) }}</h3>
              <strong>{{ formatImpact(selected) }}</strong>
            </div>
            <div>
              <Badge :tone="selectedOutcome.tone">{{ t(selectedOutcome.labelKey) }}</Badge>
              <Badge :tone="selectedSeverity.tone">{{ t(selectedSeverity.labelKey) }}</Badge>
            </div>
          </header>
          <Select
            v-if="tabOptions.length > 1"
            v-model="detailTabModel"
            :label="t('insights.sysadmin.history.tab')"
            :options="tabOptions"
          />
          <Skeleton v-if="detailState === 'loading'" height="12rem" />
          <Alert v-else-if="detailState === 'error'" tone="danger">
            {{ t('insights.sysadmin.history.detailError') }}
          </Alert>
          <template v-else>
            <RichTextViewer v-if="selected.content" :content="selected.content" />
            <InsightElementList :elements="selected.elements" :tabs="selected.tabs ?? []" />
          </template>
        </template>
      </article>
    </div>
  </section>
</template>

<style scoped>
.history-panel .history-panel__groups section {
  display: grid;
  gap: var(--ui-space-md);
  min-width: 0;
}
.history-panel .history-panel__detail header {
  display: flex;
  gap: var(--ui-space-xs);
  justify-content: space-between;
}
.history-panel .history-panel__detail header > div:last-child {
  display: flex;
  gap: var(--ui-space-xs);
}
.history-panel h3 {
  margin: 0;
}
.history-panel h4 {
  margin: 0;
}
.history-panel p {
  margin: 0;
}
.history-panel .history-panel__detail header > div:first-child {
  display: grid;
  gap: var(--ui-space-2xs);
}

.history-panel__result.is-selected {
  border-color: var(--ui-color-primary);
  background-color: var(--ui-color-primary-subtle);
}
</style>
